import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Typography,
  Layout,
  Menu,
  Button,
  Modal,
  Input,
  Segmented,
  message,
  Spin,
  Dropdown,
  Skeleton,
  Row,
  Col,
  Tabs,
  Tooltip,
  Progress,
  Checkbox,
  Divider,
  Table,
  Collapse,
  Breadcrumb,
  Tag,
  Affix,
  Switch,
  Space,
  Grid,
  Drawer,
} from 'antd';
import {
  LockOutlined,
  InfoCircleOutlined,
  RadarChartOutlined,
  CaretDownOutlined,
  ContactsOutlined,
  BulbOutlined,
  ProductOutlined,
  BarsOutlined,
  EditOutlined,
  CopyOutlined,
  CodeOutlined,
  RobotOutlined,
  FormOutlined,
  DatabaseOutlined,
  MenuOutlined,
  ClusterOutlined,
  ExportOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { Radar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import StructuredHeadings from './StructuredHeadings';
import AuthenticatedMenu from '../../components/AuthenticatedMenu';
import './ArticleDetail.css';
import { getUserRole, getUserProfile } from '../../auth';
import { setAuthToken } from '../../api';
import moment from 'moment-timezone';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  ChartTooltip,
  Legend,
);

const { Title, Text, Paragraph } = Typography;
const { Header, Content, Sider, Footer } = Layout;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { useBreakpoint } = Grid;

const SharedArticle = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topTopics, setTopTopics] = useState([]);
  const [uniqueTopics, setUniqueTopics] = useState([]);
  const [relatedKeywords, setRelatedKeywords] = useState([]);
  const [queryInsightsSummaries, setQueryInsightsSummaries] = useState({});
  const [topicCheckStatus, setTopicCheckStatus] = useState('idle');
  const [contentGenerationStatus, setContentGenerationStatus] =
    useState('idle');
  const { uuid } = useParams();
  const [products, setProducts] = useState([]);
  const [selectedTab, setSelectedTab] = useState('relatedKeywords');
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [showSources, setShowSources] = useState(true);
  const [lastJobStatus, setLastJobStatus] = useState({
    topicCheck: null,
    contentGeneration: null,
  });
  const [editableTitle, setEditableTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [shareStatus, setShareStatus] = useState('private');
  const [shareUrl, setShareUrl] = useState('');
  const [sharePassword, setSharePassword] = useState('');
  const [userPlan, setUserPlan] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // ticketCount は廃止（クレジット制に移行）
  const [userRole, setUserRole] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [activeKey, setActiveKey] = useState('1');
  const contentRef = useRef(null);
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const utmParams = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'ref'].forEach(
      param => {
        const value = searchParams.get(param);
        if (value) {
          utmParams[param] = value;
        }
      },
    );

    // SharedArticleのパスを含むfrom_pageを設定
    utmParams.from_page = location.pathname + location.search;

    if (Object.keys(utmParams).length > 0) {
      localStorage.setItem('utmParams', JSON.stringify(utmParams));
    }
  }, [location]);

  useEffect(() => {
    const fetchArticleAndStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          setAuthToken(token);
          try {
            const profile = await getUserProfile();
            setIsAuthenticated(true);
            setUserProfile(profile);
            setUserRole(profile.role);
          } catch (error) {
            console.error('Failed to get user profile:', error);
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }

        const articleResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/shared/${uuid}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            params: isPasswordVerified ? { password } : {},
          },
        );

        if (!isPasswordVerified && articleResponse.data.password_required) {
          setIsPasswordRequired(true);
        } else {
          setArticle(articleResponse.data);
          parseArticleData(articleResponse.data);
          if (!isEditingTitle) {
            setEditableTitle(articleResponse.data.title);
          }
          setShareStatus(
            prevStatus =>
              articleResponse.data.share_status || prevStatus || 'private',
          );
          setShareUrl(prevUrl => {
            if (articleResponse.data.share_status === 'private') {
              return '';
            }
            return articleResponse.data.share_url || prevUrl || '';
          });

          // Products data fetch
          const productsResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/seo-article/products/${uuid}`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            },
          );
          setProducts(productsResponse.data);

          if (isAuthenticated) {
            const statusResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/seo-article/status/${uuid}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            setTopicCheckStatus(statusResponse.data.topic_check_status);
            setContentGenerationStatus(
              statusResponse.data.content_generation_status,
            );
            setLastJobStatus({
              topicCheck: statusResponse.data.last_topic_check_status,
              contentGeneration:
                statusResponse.data.last_content_generation_status,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch article or status:', error);
        message.error('記事情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchArticleAndStatus();
  }, [uuid, isEditingTitle, shareStatus, isPasswordVerified, password]);

  const parseArticleData = data => {
    if (data.top_topics) {
      const parsedTopTopics =
        typeof data.top_topics === 'string'
          ? JSON.parse(data.top_topics)
          : data.top_topics;
      setTopTopics(
        parsedTopTopics.filter(
          topic => topic[0] && !topic[0].startsWith('トピック'),
        ),
      );
    }

    if (data.unique_topics) {
      const parsedUniqueTopics =
        typeof data.unique_topics === 'string'
          ? JSON.parse(data.unique_topics)
          : data.unique_topics;
      setUniqueTopics(
        parsedUniqueTopics.filter(
          topic => topic[0] && !topic[0].startsWith('トピック'),
        ),
      );
    }

    if (data.related_keywords) {
      const parsedRelatedKeywords =
        typeof data.related_keywords === 'string'
          ? JSON.parse(data.related_keywords)
          : data.related_keywords;
      setRelatedKeywords(parsedRelatedKeywords);
    }

    if (data.query_insights_summaries) {
      setQueryInsightsSummaries(JSON.parse(data.query_insights_summaries));
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/shared/${uuid}`,
        {
          params: { password },
        },
      );
      if (response.data.password_required) {
        message.error('パスワードが正しくありません');
        setPassword('');
      } else {
        setArticle(response.data);
        setIsPasswordVerified(true);
        setIsPasswordRequired(false);
        parseArticleData(response.data);
        message.success('パスワードが確認されました。記事を表示します。');
      }
    } catch (error) {
      console.error('Failed to authenticate with password:', error);
      message.error('パスワードが正しくありません');
      setPassword('');
    }
  };

  const showRegistrationModal = () => {
    setIsModalVisible(true);
  };

  const handleRegistration = () => {
    const utmParams = JSON.parse(localStorage.getItem('utmParams') || '{}');
    const searchParams = new URLSearchParams(utmParams);
    navigate(`/login?${searchParams.toString()}`);
  };

  const ReadOnlyStructuredHeadings = ({ headings, isContentGenerated }) => {
    const handleChange = () => {
      message.warning('共有された記事の構成案は編集できません。');
    };

    const handleDragStart = e => {
      e.preventDefault();
      message.warning('共有された記事の構成案は編集できません。');
    };

    return (
      <div onDragStart={handleDragStart}>
        <StructuredHeadings
          headings={headings}
          onChange={handleChange}
          isContentGenerated={isContentGenerated}
          isReadOnly={true}
        />
      </div>
    );
  };

  const searchIntents = article?.search_intents
    ? JSON.parse(article.search_intents)
    : [];
  const readerGoals = article?.reader_goals
    ? JSON.parse(article.reader_goals)
    : { content_goal: '', best_result: '' };
  const searchPersona = article?.search_persona || '';

  const queryInsightsData = {
    labels: [
      '専門性',
      'コンバージョン',
      '緊急性',
      'ローカリゼーション',
      'ニーズの多様性',
      'ビジュアライゼーション',
      '公式度と信頼性',
      '情報の最新性',
      'UGCやレビュー・体験談',
    ],
    datasets: [
      {
        label: 'Query Insights',
        data: article
          ? [
              article.expertise_and_depth,
              article.purchase_motivation_and_conversion,
              article.urgency_and_cta,
              article.localization_and_regional_adaptation,
              article.diversity_of_user_needs,
              article.visualization,
              article.source_formality_and_reliability,
              article.freshness_of_information,
              article.ugc_utilization_and_impact,
            ]
          : [],
        backgroundColor: 'rgba(0,212,255,0.1)',
        borderColor: 'rgb(0 212 255)',
        borderWidth: 2,
      },
    ],
  };

  const queryInsightsDescriptions = [
    {
      category: '専門性',
      description: '上位サイトがどの程度専門的で深い情報を提供しているか',
    },
    {
      category: 'コンバージョン',
      description:
        '上位サイトがどの程度購買意欲を喚起し、コンバージョンを促進しているか',
    },
    {
      category: '緊急性',
      description:
        '検索者が緊急を要し、また上位サイトが即座の行動を促しているか',
    },
    {
      category: 'ローカリゼーション',
      description: '国や地域に根付いたローカルな情報である度合い',
    },
    {
      category: 'ニーズの多様性',
      description: 'どの程度、多様なユーザーニーズを内容したキーワードであるか',
    },
    {
      category: 'ビジュアライゼーション',
      description: '上位サイトがどの程度ビジュアルコンテンツを提供しているか',
    },
    {
      category: '公式度と信頼性',
      description: '上位サイトの情報源がどの程度公式で信頼できるか',
    },
    {
      category: '情報の最新性',
      description: '上位サイトがどの程度最新の情報を提供しているか',
    },
    {
      category: 'UGCやレビュー・体験談',
      description:
        '上位サイトがどの程度ユーザー生成コンテンツを活用し、影響力があるか',
    },
  ];

  const categoryColors = {
    '企業/ブランド': { backgroundColor: '#e6f7ff', color: '#1890ff' },
    '商品/サービス': { backgroundColor: '#fff2e8', color: '#fa8c16' },
    '機能/プラン': { backgroundColor: '#f9f0ff', color: '#7b61ff' },
    ウェブサイト: { backgroundColor: '#f8f9cd', color: '#b5b01f' },
    '店舗/施設': { backgroundColor: '#f0fff1', color: '#33b65e' },
    '書籍/コンテンツ': { backgroundColor: '#f3e4ee', color: '#c76fbf' },
    公的機関: { backgroundColor: '#fffbe6', color: '#faad14' },
    その他: { backgroundColor: '#f0f0f0', color: '#8c8c8c' },
  };

  const productsColumns = [
    {
      title: '関連スコア',
      dataIndex: 'article_data',
      key: 'relevance',
      render: (article_data, record) => {
        const relevance = article_data[article.id]?.relevance || 0;
        return (
          <Progress
            percent={relevance * 20}
            steps={5}
            size={7}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
            format={() => null}
          />
        );
      },
      width: 120,
    },
    {
      title: 'プロダクト名',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'カテゴリー',
      dataIndex: 'category',
      key: 'category',
      render: category => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor:
              categoryColors[category]?.backgroundColor || '#f0f0f0',
            color: categoryColors[category]?.color || '#000',
            fontSize: '12px',
            width: '100px',
            textAlign: 'center ',
          }}
        >
          {category}
        </span>
      ),
      width: 180,
    },
    {
      title: '説明文',
      dataIndex: 'article_data',
      key: 'description',
      render: (article_data, record) =>
        article_data[article.id]?.description || '',
      width: 240,
    },
  ];

  const renderStructuredHeadings = headings => {
    if (!headings) return null;
    const lines = headings.split('\n');
    let h2Count = 0;
    let h3Count = 0;

    return (
      <ul className="table-of-contents">
        {lines.map((line, index) => {
          if (line.startsWith('$$')) {
            h2Count++;
            h3Count = 0;
            const headingText = line.slice(2).trim();
            const headingId = `content-h2-${h2Count}`;
            return (
              <li key={index} className="toc-parent">
                <a
                  href={`#${headingId}`}
                  onClick={e => {
                    e.preventDefault();
                    scrollToHeading(headingId);
                  }}
                >
                  {headingText}
                </a>
              </li>
            );
          } else if (line.startsWith('###')) {
            h3Count++;
            const headingText = line.slice(3).trim();
            const headingId = `content-h2-${h2Count}-h3-${h3Count}`;
            return (
              <li key={index}>
                <a
                  href={`#${headingId}`}
                  onClick={e => {
                    e.preventDefault();
                    scrollToHeading(headingId);
                  }}
                >
                  {headingText}
                </a>
              </li>
            );
          }
          return null;
        })}
      </ul>
    );
  };

  const scrollToHeading = headingId => {
    const element = document.getElementById(headingId);
    if (element) {
      const offset = 0;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // copyHtml 関数はおそらく ArticleDetail.js と同様のものがここにもあると想定
  const copyHtml = () => {
    if (!article || !article.content) return;
    let formattedHtml = article.content;
    if (!showSources) {
      formattedHtml = formattedHtml.replace(/<sup[^>]*>.*?<\/sup>/g, '');
      formattedHtml = formattedHtml.replace(/\[\d+\]/g, '');
      formattedHtml = formattedHtml.replace(
        /<a[^>]*class="citation-ref"[^>]*>.*?<\/a>/g,
        '',
      );
      formattedHtml = formattedHtml.replace(
        /<a[^>]*data-citation[^>]*>.*?<\/a>/g,
        '',
      );
    }
    formattedHtml = formattedHtml.replace(/></g, '>\n<');
    if (article.citations && article.citations.length > 0 && showSources) {
      formattedHtml +=
        '\n\n<h2 id="references">参考文献</h2>\n<ul class="references-list">\n';
      article.citations.forEach(citation => {
        formattedHtml += `  <li><a href="${citation.url}" target="_blank" rel="noopener noreferrer">[${citation.id}] ${getDomainName(citation.url)}</a></li>\n`;
      });
      formattedHtml += '</ul>';
    }
    navigator.clipboard
      .writeText(formattedHtml)
      .then(() => {
        message.success('HTMLをコピーしました');
      })
      .catch(error => {
        message.error('HTMLのコピーに失敗しました');
        console.error('Failed to copy HTML: ', error);
      });
  };

  // copyText 関数を ArticleDetail.js と同様のロジックで修正
  const copyText = () => {
    if (!article || !article.content) return;

    const tempDiv = document.createElement('div');

    // タイトルを追加
    const titleElement = document.createElement('h1');
    titleElement.textContent = article.title;
    tempDiv.appendChild(titleElement);

    // アイキャッチ画像のURLテキスト挿入処理を削除 (元々あれば)

    // 本文を追加する前に、画像タグを除去する
    let contentHtml = article.content;
    contentHtml = contentHtml.replace(/<img[^>]*>/g, '[画像]'); // 画像があった箇所に目印

    // ソース参照を非表示にする場合は[n]と関連タグを削除
    if (!showSources) {
      contentHtml = contentHtml.replace(
        /<sup><a [^>]*href=[^>]*>[\s\S]*?\[\d+\][\s\S]*?<\/a><\/sup>/g,
        '',
      );
      contentHtml = contentHtml.replace(/\[\d+\]/g, '');
    }
    tempDiv.innerHTML += contentHtml;

    // 参考文献を追加
    if (article.citations && article.citations.length > 0 && showSources) {
      const referencesHeading = document.createElement('h2');
      referencesHeading.textContent = '参考文献';
      tempDiv.appendChild(referencesHeading);
      const referencesList = document.createElement('ul');
      article.citations.forEach(citation => {
        const listItem = document.createElement('li');
        listItem.textContent = `[${citation.id}] ${citation.url}`;
        referencesList.appendChild(listItem);
      });
      tempDiv.appendChild(referencesList);
    }

    // テーブル後に空行を追加
    const tables = tempDiv.getElementsByTagName('table');
    for (let i = 0; i < tables.length; i++) {
      const br = document.createElement('br');
      tables[i].parentNode.insertBefore(br, tables[i].nextSibling);
    }

    // スタイルを適用
    const style = document.createElement('style');
    style.textContent = `
      h1, h2, h3, h4 { font-weight: bold !important; }
      table { border-collapse: collapse !important; width: 100% !important; margin-bottom: 16px !important; }
      th, td { border: 1px solid black !important; padding: 4px !important; }
      img { max-width: 100% !important; height: auto !important; margin: 10px 0 !important; }
    `;
    tempDiv.appendChild(style);

    const hiddenElement = document.createElement('div');
    hiddenElement.innerHTML = tempDiv.innerHTML;
    hiddenElement.style.position = 'fixed';
    hiddenElement.style.pointerEvents = 'none';
    hiddenElement.style.opacity = '0';
    document.body.appendChild(hiddenElement);

    const range = document.createRange();
    range.selectNode(hiddenElement);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);

    try {
      document.execCommand('copy');
      message.success(
        'Googleドキュメント用にコピーしました。画像は別途コピーして貼り付けてください。',
      );
    } catch (err) {
      message.error('コピーに失敗しました');
      console.error('Failed to copy text: ', err);
    }

    window.getSelection().removeAllRanges();
    document.body.removeChild(hiddenElement);
  };

  const ArticleContent = ({ article }) => {
    if (!article || !article.content) return null;
    const contentRef = useRef(null); // refを作成

    // ソース参照[n]を非表示にする処理
    const processContent = content => {
      if (!showSources) {
        // Updated regex to better handle variations of citation links
        let processed = content.replace(
          /<sup><a [^>]*href=[^>]*>[\s\S]*?\[\d+\][\s\S]*?<\/a><\/sup>/g,
          '',
        ); // Remove entire sup tag with link
        processed = processed.replace(/\[\d+\]/g, ''); // Remove any remaining bracketed numbers
        return processed;
      }
      return content;
    };

    const renderCitations = () => {
      if (
        !showSources ||
        !article.citations ||
        article.citations.length === 0
      ) {
        return null;
      }
      const extractReferencedCitations = content => {
        try {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = content;
          const links = tempDiv.querySelectorAll('a');
          const ids = [];
          links.forEach(link => {
            const match = link.textContent.match(/^\s*\\[(\d+)\\]\s*$/);
            if (match) {
              ids.push(parseInt(match[1], 10));
            }
          });
          const uniqueIds = [...new Set(ids)];
          return uniqueIds.filter(id => !isNaN(id));
        } catch (error) {
          console.error('Error extracting citations:', error);
          return [];
        }
      };

      const referencedIds = article.content
        ? extractReferencedCitations(article.content)
        : [];

      return (
        <div className="article-citations">
          <h2 id="references">
            参考文献
            <Tooltip title="本文作成時に参考にしたウェブページです。番号は記事内の[n]と対応しています。青色表示されているのは本文中で直接引用されているサイトです。">
              <InfoCircleOutlined
                style={{
                  marginLeft: '6px',
                  fontSize: '14px',
                  color: 'rgba(0, 0, 0, 0.45)',
                }}
              />
            </Tooltip>
          </h2>
          <div className="citations-list">
            {article.citations.map((citation, index) => {
              const citationId = parseInt(String(citation.id).trim(), 10);
              const isReferenced = referencedIds.includes(citationId);
              return (
                <div key={index} className="citation-item">
                  <div className="citation-favicon">
                    <img
                      src={getFaviconUrl(citation.url)}
                      alt=""
                      onError={e => {
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/favicon.ico`;
                      }}
                    />
                  </div>
                  <div className="citation-content">
                    <a
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="citation-link"
                    >
                      <span className="citation-number">[{citation.id}]</span>
                      <span
                        className="citation-domain"
                        style={{ color: isReferenced ? '#1890ff' : '#8c8c8c' }}
                      >
                        {getDomainName(citation.url)}
                      </span>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    const getFaviconUrl = url => {
      try {
        const domain = new URL(url).hostname;
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
      } catch (error) {
        return `${process.env.PUBLIC_URL}/favicon.ico`;
      }
    };

    const parser = new DOMParser();
    const doc = parser.parseFromString(article.content, 'text/html');
    const firstH2 = doc.querySelector('h2');
    const introduction = firstH2
      ? article.content.substring(0, article.content.indexOf(firstH2.outerHTML))
      : article.content;
    const mainContentHtml = firstH2
      ? article.content.substring(article.content.indexOf(firstH2.outerHTML))
      : '';

    useEffect(() => {
      // article.content が更新されてDOMに反映された後に実行
      if (contentRef.current && article.content) {
        const images = contentRef.current.querySelectorAll('img');
        images.forEach(img => {
          const currentSrc = img.getAttribute('src');
          // セクション見出し画像 (相対パス /static/images/heading_...) を対象とする
          // アイキャッチ画像 (既に絶対URL /static/images/eyecatch_...) は対象外とするため、 srcが /static/ で始まり、かつ API_URL で始まらないものを対象とする
          if (
            currentSrc &&
            currentSrc.startsWith('/static/images/') &&
            !currentSrc.startsWith(process.env.REACT_APP_API_URL)
          ) {
            // heading_ というファイル名パターンも条件に加えることでより限定的にする
            if (currentSrc.includes('/heading_')) {
              img.src = `${process.env.REACT_APP_API_URL}${currentSrc}`;
              console.log('Updated section image src to:', img.src);
            }
          }
        });
      }
    }, [article.content, showSources]); // showSourcesも依存配列に追加

    const addIdsToHeadings = htmlContent => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      let h2Count = 0;
      let h3Count = 0;

      doc.querySelectorAll('h2, h3, h4').forEach(heading => {
        if (heading.tagName === 'H2') {
          h2Count++;
          h3Count = 0;
          heading.id = `content-h2-${h2Count}`;
        } else if (heading.tagName === 'H3') {
          h3Count++;
          heading.id = `content-h2-${h2Count}-h3-${h3Count}`;
        } else if (heading.tagName === 'H4') {
          heading.id = `content-h2-${h2Count}-h3-${h3Count}-h4-${heading.textContent.replace(/\s+/g, '-').toLowerCase()}`;
        }
      });
      return doc.body.innerHTML;
    };

    return (
      <div className="article-content">
        <div
          className="html-content"
          dangerouslySetInnerHTML={{
            __html: addIdsToHeadings(processContent(introduction)),
          }}
        />
        {firstH2 && (
          <div className="table-of-contents">
            <div className="toc-title">この記事の目次</div>
            {renderStructuredHeadings(article.structured_headings)}
          </div>
        )}
        {mainContentHtml && (
          <div
            ref={contentRef} // メインコンテンツのdivにrefを設定
            className="html-content"
            dangerouslySetInnerHTML={{
              __html: addIdsToHeadings(processContent(mainContentHtml)),
            }}
          />
        )}
        {renderCitations()}
      </div>
    );
  };

  const fixedColumn30 = {
    width: 30,
    paddingLeft: 8,
    paddingRight: 8,
  };
  const fixedColumn50 = {
    width: 50,
    paddingLeft: 8,
    paddingRight: 8,
  };
  const fixedColumn240 = {
    width: 240,
    paddingLeft: 16,
    paddingRight: 16,
  };

  // HTMLコンテンツの文字数を計算する関数
  const calculateHtmlCharacterCount = htmlContent => {
    if (!htmlContent) return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent.replace(/\s+/g, '').length;
  };

  // ドメイン名を取得する関数
  const getDomainName = url => {
    try {
      const hostname = new URL(url).hostname;
      return hostname.replace(/^www\./, '');
    } catch (error) {
      return url;
    }
  };

  const handleCopyStructure = () => {
    if (article.structured_headings) {
      const formattedStructure = article.structured_headings
        .split('\n')
        .map(line => {
          if (line.startsWith('$$')) {
            return `<h2>${line.slice(2).trim()}</h2>`;
          } else if (line.startsWith('###')) {
            return `<h3>${line.slice(3).trim()}</h3>`;
          }
          return line;
        })
        .join('\n');

      navigator.clipboard
        .writeText(formattedStructure)
        .then(() => {
          message.success('構成案をコピーしました！');
        })
        .catch(error => {
          console.error('Failed to copy structure:', error);
          message.error('構成案のコピーに失敗しました');
        });
    }
  };

  const handleTabChange = key => {
    setActiveKey(key);
    if (contentRef.current && !screens.lg) {
      contentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const tabItems = [
    {
      key: '1',
      icon: <RadarChartOutlined />,
      title: '上位サイトの傾向',
      tooltip:
        '検索上位表示サイトの共通点や特徴を9つの観点から分析し、レーダーチャートで可視化します。記事作成の方向性を決める重要な指標です。',
    },
    {
      key: '2',
      icon: <ContactsOutlined />,
      title: '検索者の分析',
      tooltip:
        'キーワードを検索するユーザーの意図や関心事、悩みなど深層心理を分析します。検索者が求めている情報を把握してターゲット層に合った記事を作成できます。',
    },
    {
      key: '3',
      icon: <BulbOutlined />,
      title: 'トピック分析',
      tooltip:
        '上位表示サイトで頻出するトピックや特有のキーワードを抽出し、記事に盛り込むべき内容を特定します。SEO上重要な語句が一目でわかります。',
    },
    {
      key: '4',
      icon: <ProductOutlined />,
      title: 'プロダクト分析',
      tooltip:
        '検索結果で言及されている商品やサービスを抽出した結果です。製品レビューや比較記事を作成する際に参考になる情報を提供します。',
    },
    {
      key: '5',
      icon: <BarsOutlined />,
      title: '構成案の生成',
      tooltip:
        '分析結果に基づいて最適な記事構成を作成します。見出し構造や含めるべきトピックを提案し、SEO効果の高い記事のフレームワークを提供します。',
    },
    {
      key: '6',
      icon: <EditOutlined />,
      title: '本文の生成',
      tooltip:
        '構成案に沿って専門性、正確性、独自性の高い本文を生成します。SEO効果と読者の満足度を両立した高品質な記事を作成します。',
    },
  ];

  const MobileTabMenu = ({ activeKey, onChange, items }) => {
    return (
      <Menu
        mode="horizontal"
        selectedKeys={[activeKey]}
        onClick={({ key }) => onChange(key)}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-around',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          zIndex: 1000,
        }}
        className="mobileMenu"
      >
        {items.map(item => (
          <Menu.Item key={item.key} icon={item.icon} />
        ))}
      </Menu>
    );
  };

  const renderTabContent = key => {
    const item = tabItems.find(item => item.key === key);
    return (
      <div ref={contentRef}>
        {!screens.lg && (
          <Title
            level={3}
            style={{ marginBottom: '20px' }}
            className="mobile_tabTitle"
          >
            {item.title}
          </Title>
        )}
        {key === '1' && (
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              {screens.lg && (
                <Radar
                  data={{
                    labels: queryInsightsData.labels,
                    datasets: queryInsightsData.datasets,
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      r: {
                        min: 0,
                        max: 5,
                        ticks: {
                          stepSize: 1,
                          font: {
                            size: 12,
                          },
                        },
                        pointLabels: {
                          font: {
                            size: 12,
                          },
                        },
                      },
                    },
                  }}
                />
              )}
            </Col>
            <Col xs={24} lg={12} className="queryInsight">
              <Title
                level={3}
                style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
              >
                上位サイトの傾向
                <Tooltip title="上位サイトの特徴を分析し、9つの観点からAIがスコア化した独自の指標です。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              <Collapse
                defaultActiveKey={['0']}
                accordion
                expandIconPosition="left"
                bordered={false}
              >
                {queryInsightsDescriptions.map((item, index) => (
                  <Panel
                    header={
                      <div
                        style={{
                          display: 'grid',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          gridTemplateColumns: '5fr 4fr',
                        }}
                      >
                        <span className="qi_categoryname">{item.category}</span>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: '10px',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Progress
                            percent={
                              queryInsightsData.datasets[0].data[index] * 20
                            }
                            steps={5}
                            size="small"
                            strokeColor={{
                              '0%': '#108ee9',
                              '100%': '#87d068',
                            }}
                            style={{ width: '28px', marginRight: '10px' }}
                            format={() => ''} // この行を追加してn%を非表示にします
                          />
                          <span style={{ marginRight: '10px' }}>
                            <span
                              style={{
                                color:
                                  queryInsightsData.datasets[0].data[index] >=
                                  4.0
                                    ? '#0072ff'
                                    : '#00d4ff',
                              }}
                            >
                              {queryInsightsData.datasets[0].data[
                                index
                              ].toFixed(1)}
                            </span>
                            <span style={{ color: '#999' }}> / 5.0</span>
                          </span>
                          <Tooltip title={item.description}>
                            <InfoCircleOutlined
                              style={{ color: 'rgba(0, 0, 0, 0.45)' }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    }
                    key={index}
                    style={{
                      borderBottom:
                        index === queryInsightsDescriptions.length - 1
                          ? 'none'
                          : '1px solid #f0f0f0',
                      transition: 'background-color 0.3s',
                    }}
                  >
                    <p>{queryInsightsSummaries[item.category] || ''}</p>
                  </Panel>
                ))}
              </Collapse>
            </Col>
          </Row>
        )}
        {key === '2' && (
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Title
                level={3}
                style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
              >
                分析サマリー
                <Tooltip title="検索結果やオープンデータを多角的に分析し、検索者が知りたい可能性が高い情報を要約したデータです。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              {article && article.summarized_headings && (
                <div
                  style={{
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    background:
                      'linear-gradient(135deg, rgba(0, 212, 255, 0.02) 0%, rgba(0, 114, 255, 0.02) 100%)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <Paragraph
                    className="summary-content"
                    style={{
                      whiteSpace: 'pre-wrap',
                      background:
                        'linear-gradient(135deg, rgb(15, 170, 201) 0%, rgb(38, 126, 233) 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      color: 'transparent',
                      fontSize: '15px',
                      lineHeight: '1.8',
                      fontWeight: '500',
                    }}
                  >
                    {article.summarized_headings ? (
                      <div
                        className="summary-content-inner"
                        style={{
                          color: 'rgb(15, 170, 201)',
                          fontSize: '15px',
                          lineHeight: '1.8',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: article.summarized_headings
                            // 見出し（## ）を【】で囲んで太字に変換
                            .replace(
                              /## (.*?)$/gm,
                              '【<strong>$1</strong>】<br>',
                            )
                            // 箇条書き（$$）を・に変換
                            .replace(/\$\$\s*(.*?)(\r?\n|$)/g, '・$1<br>')
                            // 引用番号[n]を削除
                            .replace(/\[\d+\]/g, ''),
                        }}
                      />
                    ) : null}
                  </Paragraph>
                </div>
              )}
            </Col>
            <Col xs={24} lg={12}>
              <Title
                level={3}
                style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
              >
                検索意図
                <Tooltip title="キーワードに内包される検索者の知りたいことをAIが分析したデータです。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              {article && article.search_intents && (
                <Table
                  bordered
                  dataSource={searchIntents.map((intent, index) => ({
                    key: index,
                    index: index + 1,
                    intent: intent.replace(/"/g, ''),
                  }))}
                  columns={[
                    {
                      dataIndex: 'index',
                      key: 'index',
                      render: (text, record, index) => ({
                        children: text,
                        props: {
                          style: {
                            backgroundColor: '#fafafa',
                            padding: '16px',
                            fontWeight: 'bold',
                          },
                        },
                      }),
                    },
                    {
                      dataIndex: 'intent',
                      key: 'intent',
                    },
                  ]}
                  showHeader={false}
                  pagination={false}
                />
              )}
              <Title
                level={3}
                style={{
                  fontSize: '14px',
                  marginTop: '36px',
                  color: 'rgb(0 212 255)',
                }}
              >
                読者のゴール
                <Tooltip title="検索行動の後で、読者がどのような状態になりたいかをAIが分析したデータです。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              {article && article.reader_goals && (
                <Table
                  bordered
                  dataSource={[
                    {
                      key: 'content_goal',
                      label: 'コンテンツのゴール',
                      value: readerGoals.content_goal,
                    },
                    {
                      key: 'best_result',
                      label: '最高の結果',
                      value: readerGoals.best_result,
                    },
                  ]}
                  columns={[
                    {
                      dataIndex: 'label',
                      key: 'label',
                      render: (text, record, index) => ({
                        children: text,
                        props: {
                          style: {
                            backgroundColor: '#fafafa',
                            padding: '16px',
                            fontWeight: 'bold',
                          },
                        },
                      }),
                    },
                    {
                      dataIndex: 'value',
                      key: 'value',
                    },
                  ]}
                  showHeader={false}
                  pagination={false}
                />
              )}
              <Title
                level={3}
                style={{
                  fontSize: '14px',
                  marginTop: '36px',
                  color: 'rgb(0 212 255)',
                }}
              >
                検索者のペルソナ
                <Tooltip title="AIが多角的に分析した、中央値的な検索者のペルソナです。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              {article && article.search_persona && (
                <Table
                  bordered
                  dataSource={searchPersona
                    .split('\n')
                    .map((line, index) => {
                      const [key, value] = line.split(':');
                      return key && value
                        ? { key: index, label: key.trim(), value: value.trim() }
                        : null;
                    })
                    .filter(Boolean)}
                  columns={[
                    {
                      dataIndex: 'label',
                      key: 'label',
                      width: 160,
                      render: (text, record, index) => ({
                        children: text,
                        props: {
                          style: {
                            backgroundColor: '#fafafa',
                            padding: '16px',
                            fontWeight: 'bold',
                          },
                        },
                      }),
                    },
                    {
                      dataIndex: 'value',
                      key: 'value',
                    },
                  ]}
                  showHeader={false}
                  pagination={false}
                />
              )}
            </Col>
          </Row>
        )}
        {key === '3' && (
          <Row gutter={[24, 36]}>
            <Col xs={24} xl={8}>
              <Title
                level={3}
                style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
              >
                重要語句
                <Tooltip title="上位サイトで頻出している重要な語句です。一般的に用いられるサジェストワードやLSIと比べ、ある意味ではよりリアルなデータと言えます。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              <Table
                columns={[
                  {
                    title: '',
                    dataIndex: 'checked',
                    key: 'check',
                    render: (_, record) => {
                      const keyword = record[0];
                      const checkState = record[2];
                      const checkboxColor =
                        checkState === 1
                          ? 'pink'
                          : checkState === 2
                            ? 'red'
                            : checkState === 3
                              ? 'orange'
                              : 'default';
                      return (
                        <Checkbox
                          checked={checkState !== 0}
                          disabled={true}
                          className={`custom-checkbox ${checkboxColor}`}
                        />
                      );
                    },
                    ...fixedColumn30,
                  },
                  {
                    title: 'キーワード',
                    dataIndex: 'keyword',
                    key: 'keyword',
                    render: (_, record) => (
                      <span style={{ color: record[2] ? '#ccc' : 'inherit' }}>
                        {record[0]}
                      </span>
                    ),
                  },
                  {
                    title: '出現割合',
                    dataIndex: 'count',
                    key: 'count',
                    render: (_, record) => (
                      <Progress
                        percent={record[1] * 20}
                        steps={5}
                        size={7}
                        strokeColor={{
                          '0%': '#108ee9',
                          '100%': '#87d068',
                        }}
                        format={() => null}
                      />
                    ),
                    ...fixedColumn50,
                  },
                ]}
                dataSource={relatedKeywords}
                className="topic-table"
                pagination={false}
              />
            </Col>
            <Col xs={24} xl={8}>
              <Title
                level={3}
                style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
              >
                上位サイトの主要トピック
                <Tooltip title="上位サイトのコンテンツをAIが分析し、頻出しているトピックを整理したデータです。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              <Table
                columns={[
                  {
                    title: '',
                    dataIndex: 'checked',
                    key: 'check',
                    render: (_, record) => {
                      const checkState = record[2];
                      const checkboxColor =
                        checkState === 1
                          ? 'pink'
                          : checkState === 2
                            ? 'red'
                            : checkState === 3
                              ? 'orange'
                              : 'default';
                      return (
                        <Checkbox
                          checked={checkState !== 0}
                          disabled={true}
                          className={`custom-checkbox ${checkboxColor}`}
                        />
                      );
                    },
                    ...fixedColumn30,
                  },
                  {
                    title: 'トピック',
                    dataIndex: 'topic',
                    key: 'topic',
                    render: (_, record) => (
                      <span style={{ color: record[2] ? '#ccc' : 'inherit' }}>
                        {record[0]}
                      </span>
                    ),
                  },
                  {
                    title: '頻出度',
                    dataIndex: 'frequency',
                    key: 'frequency',
                    render: (_, record) => (
                      <Progress
                        percent={record[1] * 20}
                        steps={5}
                        size={7}
                        strokeColor={{
                          '0%': '#108ee9',
                          '100%': '#87d068',
                        }}
                        format={() => null}
                      />
                    ),
                    ...fixedColumn50,
                  },
                ]}
                dataSource={topTopics}
                className="topic-table"
                pagination={false}
              />
            </Col>
            <Col xs={24} xl={8}>
              <Title
                level={3}
                style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
              >
                独自性を出しやすいトピック
                <Tooltip title="上位サイトが扱っていないが、一定のニーズがある可能性あるトピックのアイデアです。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              <Table
                columns={[
                  {
                    title: '',
                    dataIndex: 'checked',
                    key: 'check',
                    render: (_, record) => {
                      const checkState = record[2];
                      const checkboxColor =
                        checkState === 1
                          ? 'pink'
                          : checkState === 2
                            ? 'red'
                            : checkState === 3
                              ? 'orange'
                              : 'default';
                      return (
                        <Checkbox
                          checked={checkState !== 0}
                          disabled={true}
                          className={`custom-checkbox ${checkboxColor}`}
                        />
                      );
                    },
                    ...fixedColumn30,
                  },
                  {
                    title: 'トピック',
                    dataIndex: 'topic',
                    key: 'topic',
                    render: (_, record) => (
                      <span style={{ color: record[2] ? '#ccc' : 'inherit' }}>
                        {record[0]}
                      </span>
                    ),
                  },
                  {
                    title: '独自性',
                    dataIndex: 'uniqueness',
                    key: 'uniqueness',
                    render: (_, record) => (
                      <Progress
                        percent={record[1] * 20}
                        steps={5}
                        size={7}
                        strokeColor={{
                          '0%': '#108ee9',
                          '100%': '#87d068',
                        }}
                        format={() => null}
                      />
                    ),
                    ...fixedColumn50,
                  },
                ]}
                dataSource={uniqueTopics}
                className="topic-table"
                pagination={false}
              />
            </Col>
          </Row>
        )}
        {key === '4' && (
          <>
            <Title
              level={3}
              style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
            >
              上位サイトで紹介されているプロダクト
              <Tooltip title="上位サイトで紹介されている商品やサービス、機関などをAIが分析したデータです。">
                <InfoCircleOutlined
                  style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                />
              </Tooltip>
              ：
            </Title>
            {products && products.length > 0 && (
              <Table
                dataSource={products.sort(
                  (a, b) =>
                    (b.article_data[article.id]?.presence || 0) -
                    (a.article_data[article.id]?.presence || 0),
                )}
                columns={productsColumns}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            )}
          </>
        )}
        {key === '5' && (
          <Row gutter={[24, 40]}>
            <Col xs={24} lg={14}>
              <Title
                level={3}
                style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
              >
                AIタイトル
                <Tooltip title="分析したあらゆる情報を元に、AIが生成した記事のタイトル候補です。">
                  <InfoCircleOutlined
                    style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                  />
                </Tooltip>
                ：
              </Title>
              <Title
                level={2}
                style={{ margin: '8px 0 36px', fontSize: '20px' }}
              >
                {article.title}
              </Title>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                }}
              >
                <Title
                  level={3}
                  style={{ fontSize: '14px', color: 'rgb(0 212 255)' }}
                >
                  AI構成案
                  <Tooltip title="あらゆる情報を元にAIが生成した記事の構成案です。">
                    <InfoCircleOutlined
                      style={{ marginLeft: '6px', color: 'rgb(0 212 255)' }}
                    />
                  </Tooltip>
                  ：
                </Title>
                <Button
                  onClick={handleCopyStructure}
                  style={{ fontSize: '12px', color: '#108ee9' }}
                >
                  <CopyOutlined style={{ fontSize: '16px' }} />
                  &nbsp;コピー
                </Button>
              </div>
              {article.structured_headings && (
                <ReadOnlyStructuredHeadings
                  headings={article.structured_headings}
                  isContentGenerated={
                    article.content !== null && article.content !== ''
                  }
                />
              )}
            </Col>
            <Col xs={24} lg={10}>
              <Affix offsetTop={24}>
                <div
                  className="content-check-table-wrap"
                  style={{ marginBottom: '16px' }}
                >
                  <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                    <Switch
                      checked={showAllTopics}
                      onChange={checked => setShowAllTopics(checked)}
                      size="small"
                    />
                    <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                      すべて表示
                    </span>
                  </div>
                  <Segmented
                    options={[
                      {
                        label: '重要語句',
                        value: 'relatedKeywords',
                      },
                      {
                        label: '主要トピック',
                        value: 'topTopics',
                      },
                      {
                        label: '独自トピック',
                        value: 'uniqueTopics',
                      },
                    ]}
                    value={selectedTab}
                    onChange={value => {
                      setSelectedTab(value);
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '4px',
                    }}
                    block
                  />
                  {selectedTab === 'relatedKeywords' && (
                    <Table
                      columns={[
                        {
                          title: 'チェック',
                          dataIndex: 'checked',
                          key: 'check',
                          width: '10%',
                          render: (_, record) => {
                            const keyword = record[0];
                            const checkState = record[2];
                            const checkboxColor =
                              checkState === 1
                                ? 'pink'
                                : checkState === 2
                                  ? 'red'
                                  : checkState === 3
                                    ? 'orange'
                                    : 'default';
                            return (
                              <Checkbox
                                checked={checkState !== 0}
                                disabled={true}
                                className={`custom-checkbox ${checkboxColor}`}
                              />
                            );
                          },
                          ...fixedColumn50,
                        },
                        {
                          title: 'キーワード',
                          dataIndex: 'keyword',
                          key: 'keyword',
                          width: '75%',
                          render: (_, [keyword, frequency, checked]) => (
                            <span
                              style={{
                                color: checked === 0 ? 'inherit' : '#ccc',
                              }}
                            >
                              {keyword}
                            </span>
                          ),
                        },
                        {
                          title: '重要度',
                          dataIndex: 'importance',
                          key: 'importance',
                          width: '15%',
                          render: (_, [keyword, frequency]) => (
                            <Progress
                              percent={frequency * 20}
                              steps={5}
                              size={7}
                              strokeColor={{
                                '0%': '#108ee9',
                                '100%': '#87d068',
                              }}
                              format={() => null}
                            />
                          ),
                        },
                      ]}
                      dataSource={
                        showAllTopics
                          ? relatedKeywords
                          : relatedKeywords.filter(
                              ([_, __, checked]) =>
                                checked === 0 || checked === 5,
                            )
                      }
                      pagination={false}
                      showHeader={false}
                      size="small"
                      className="content-check-table"
                    />
                  )}
                  {selectedTab === 'topTopics' && (
                    <Table
                      columns={[
                        {
                          title: 'チェック',
                          dataIndex: 'checked',
                          key: 'check',
                          width: '10%',
                          render: (_, record) => {
                            const topic = record[0];
                            const checkState = record[2];
                            const checkboxColor =
                              checkState === 1
                                ? 'pink'
                                : checkState === 2
                                  ? 'red'
                                  : checkState === 3
                                    ? 'orange'
                                    : 'default';
                            return (
                              <Checkbox
                                checked={checkState !== 0}
                                disabled={true}
                                className={`custom-checkbox ${checkboxColor}`}
                              />
                            );
                          },
                          ...fixedColumn50,
                        },
                        {
                          title: 'トピック',
                          dataIndex: 'topic',
                          key: 'topic',
                          width: '75%',
                          render: (_, [keyword, frequency, checked]) => (
                            <span
                              style={{
                                color: checked === 0 ? 'inherit' : '#ccc',
                              }}
                            >
                              {keyword}
                            </span>
                          ),
                        },
                        {
                          title: '重要度',
                          dataIndex: 'importance',
                          key: 'importance',
                          width: '15%',
                          render: (_, [keyword, frequency]) => (
                            <Progress
                              percent={frequency * 20}
                              steps={5}
                              size={7}
                              strokeColor={{
                                '0%': '#108ee9',
                                '100%': '#87d068',
                              }}
                              format={() => null}
                            />
                          ),
                        },
                      ]}
                      dataSource={
                        showAllTopics
                          ? topTopics
                          : topTopics.filter(
                              ([_, __, checked]) =>
                                checked === 0 || checked === 5,
                            )
                      }
                      pagination={false}
                      showHeader={false}
                      size="small"
                      className="content-check-table"
                    />
                  )}
                  {selectedTab === 'uniqueTopics' && (
                    <Table
                      columns={[
                        {
                          title: 'チェック',
                          dataIndex: 'checked',
                          key: 'check',
                          width: '10%',
                          render: (_, record) => {
                            const topic = record[0];
                            const checkState = record[2];
                            const checkboxColor =
                              checkState === 1
                                ? 'pink'
                                : checkState === 2
                                  ? 'red'
                                  : checkState === 3
                                    ? 'orange'
                                    : 'default';
                            return (
                              <Checkbox
                                checked={checkState !== 0}
                                disabled={true}
                                className={`custom-checkbox ${checkboxColor}`}
                              />
                            );
                          },
                          ...fixedColumn50,
                        },
                        {
                          title: 'トピック',
                          dataIndex: 'topic',
                          key: 'topic',
                          width: '75%',
                          render: (_, [keyword, uniqueness, checked]) => (
                            <span
                              style={{
                                color: checked === 0 ? 'inherit' : '#ccc',
                              }}
                            >
                              {keyword}
                            </span>
                          ),
                        },
                        {
                          title: '独自性',
                          dataIndex: 'uniqueness',
                          key: 'uniqueness',
                          width: '15%',
                          render: (_, [keyword, uniqueness]) => (
                            <Progress
                              percent={uniqueness * 20}
                              steps={5}
                              size={7}
                              strokeColor={{
                                '0%': '#108ee9',
                                '100%': '#87d068',
                              }}
                              format={() => null}
                            />
                          ),
                        },
                      ]}
                      dataSource={
                        showAllTopics
                          ? uniqueTopics
                          : uniqueTopics.filter(
                              ([_, __, checked]) =>
                                checked === 0 || checked === 5,
                            )
                      }
                      pagination={false}
                      showHeader={false}
                      size="small"
                      className="content-check-table"
                    />
                  )}
                </div>
              </Affix>
            </Col>
          </Row>
        )}
        {key === '6' && (
          <>
            <Row
              gutter={[40, 40]}
              style={{ justifyContent: 'center', margin: '0px' }}
            >
              <Col
                xs={24}
                lg={24}
                className="article_wrap"
                style={{ width: '100%', padding: '0px' }}
              >
                {article.content ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '20px',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        生成された文字数：
                        <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                          {article.content
                            ? calculateHtmlCharacterCount(
                                article.content,
                              ).toLocaleString()
                            : '0'}
                        </span>
                        文字
                      </div>
                      <Tooltip title="各文の末尾にある[n]形式のソース参照を表示/非表示にします。">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Switch
                            checked={showSources}
                            onChange={setShowSources}
                            style={{ marginRight: '8px' }}
                          />
                          <span>各センテンスのソースを表示</span>
                        </div>
                      </Tooltip>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                        marginBottom: '20px',
                      }}
                    >
                      <Tooltip title="記事のHTMLコードをコピーします。ウェブサイトに貼り付ける際に便利です。">
                        <Button
                          onClick={() => {
                            // ArticleContentコンポーネントのcopyHtml関数を直接ここで再実装
                            let formattedHtml = article.content;

                            // ソース参照を非表示にする場合は、コピー時にも参照関連のタグを削除
                            if (!showSources) {
                              // 数字の参照[n]を削除
                              formattedHtml = formattedHtml.replace(
                                /\[\d+\]/g,
                                '',
                              );

                              // 引用に関連する<sup>タグを削除
                              formattedHtml = formattedHtml.replace(
                                /<sup[^>]*>.*?<\/sup>/g,
                                '',
                              );

                              // 引用参照に使われる<a>タグで特定のクラスや属性を持つものを削除
                              formattedHtml = formattedHtml.replace(
                                /<a[^>]*class="citation-ref"[^>]*>.*?<\/a>/g,
                                '',
                              );
                              formattedHtml = formattedHtml.replace(
                                /<a[^>]*data-citation[^>]*>.*?<\/a>/g,
                                '',
                              );
                            }

                            formattedHtml = formattedHtml.replace(
                              /></g,
                              '>\n<',
                            );

                            // 参考文献がある場合はHTMLに追加（ソース表示がONの場合のみ）
                            if (
                              article.citations &&
                              article.citations.length > 0 &&
                              showSources
                            ) {
                              formattedHtml +=
                                '\n\n<h2 id="references">参考文献</h2>\n<ul class="references-list">\n';
                              article.citations.forEach(citation => {
                                formattedHtml += `  <li><a href="${citation.url}" target="_blank" rel="noopener noreferrer">[${citation.id}] ${getDomainName(citation.url)}</a></li>\n`;
                              });
                              formattedHtml += '</ul>';
                            }

                            navigator.clipboard
                              .writeText(formattedHtml)
                              .then(() => {
                                message.success('HTMLをコピーしました');
                              })
                              .catch(error => {
                                message.error('HTMLのコピーに失敗しました');
                                console.error('Failed to copy HTML: ', error);
                              });
                          }}
                          icon={<CodeOutlined />}
                          size="middle"
                          style={{ height: '36px' }}
                        >
                          HTMLをコピー
                        </Button>
                      </Tooltip>
                      <Tooltip title="記事をGoogleドキュメントに適した形式でコピーします。書式が保持されます。">
                        <Button
                          onClick={() => {
                            // ArticleContentコンポーネントのcopyText関数を直接ここで再実装
                            const tempDiv = document.createElement('div');

                            // タイトルを追加
                            const titleElement = document.createElement('h1');
                            titleElement.textContent = article.title;
                            tempDiv.appendChild(titleElement);

                            // アイキャッチ画像を追加（もし存在する場合）
                            if (article.eyecatch_image_url) {
                              const imgPlaceholder =
                                document.createElement('p');
                              imgPlaceholder.textContent = `[画像: ${process.env.REACT_APP_API_URL}${article.eyecatch_image_url}]`;
                              tempDiv.appendChild(imgPlaceholder);
                            }

                            // 本文を追加（ソース参照を非表示にする場合は[n]と関連タグを削除）
                            let contentHtml = article.content;
                            if (!showSources) {
                              // 数字の参照[n]を削除
                              contentHtml = contentHtml.replace(/\[\d+\]/g, '');

                              // 引用に関連する<sup>タグを削除
                              contentHtml = contentHtml.replace(
                                /<sup[^>]*>.*?<\/sup>/g,
                                '',
                              );

                              // 引用参照に使われる<a>タグで特定のクラスや属性を持つものを削除
                              contentHtml = contentHtml.replace(
                                /<a[^>]*class="citation-ref"[^>]*>.*?<\/a>/g,
                                '',
                              );
                              contentHtml = contentHtml.replace(
                                /<a[^>]*data-citation[^>]*>.*?<\/a>/g,
                                '',
                              );
                            }
                            tempDiv.innerHTML += contentHtml;

                            // 参考文献を追加（もし存在する場合かつソース表示がONの場合）
                            if (
                              article.citations &&
                              article.citations.length > 0 &&
                              showSources
                            ) {
                              const referencesHeading =
                                document.createElement('h2');
                              referencesHeading.textContent = '参考文献';
                              tempDiv.appendChild(referencesHeading);

                              const referencesList =
                                document.createElement('ul');
                              article.citations.forEach(citation => {
                                const listItem = document.createElement('li');
                                listItem.textContent = `[${citation.id}] ${citation.url}`;
                                referencesList.appendChild(listItem);
                              });
                              tempDiv.appendChild(referencesList);
                            }

                            // テーブル後に空行を追加
                            const tables =
                              tempDiv.getElementsByTagName('table');
                            for (let i = 0; i < tables.length; i++) {
                              const br = document.createElement('br');
                              tables[i].parentNode.insertBefore(
                                br,
                                tables[i].nextSibling,
                              );
                            }

                            // スタイルを適用
                            const style = document.createElement('style');
                            style.textContent = `
                              h1, h2, h3, h4 { font-weight: bold !important; }
                              table { border-collapse: collapse !important; width: 100% !important; margin-bottom: 16px !important; }
                              th, td { border: 1px solid black !important; padding: 4px !important; }
                              img { max-width: 100% !important; height: auto !important; margin: 10px 0 !important; }
                            `;
                            tempDiv.appendChild(style);

                            // コピー用の一時的な要素を作成
                            const hiddenElement = document.createElement('div');
                            hiddenElement.innerHTML = tempDiv.innerHTML;
                            hiddenElement.style.position = 'fixed';
                            hiddenElement.style.pointerEvents = 'none';
                            hiddenElement.style.opacity = '0';

                            // ボディに追加
                            document.body.appendChild(hiddenElement);

                            // 選択範囲を作成
                            const range = document.createRange();
                            range.selectNode(hiddenElement);
                            window.getSelection().removeAllRanges();
                            window.getSelection().addRange(range);

                            try {
                              // 選択した内容をクリップボードにコピー
                              document.execCommand('copy');
                              message.success(
                                'Googleドキュメント用にコピーしました。画像は別途コピーして貼り付けてください。',
                              );
                            } catch (err) {
                              message.error('コピーに失敗しました');
                              console.error('Failed to copy text: ', err);
                            }

                            // 選択を解除し、一時的な要素を削除
                            window.getSelection().removeAllRanges();
                            document.body.removeChild(hiddenElement);
                          }}
                          icon={<CopyOutlined />}
                          size="middle"
                          style={{ height: '36px' }}
                        >
                          Google Docs用にコピー
                        </Button>
                      </Tooltip>
                    </div>
                    <Divider />
                    <div className="article_title">{article.title}</div>
                    {article.eyecatch_image_url && (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${article.eyecatch_image_url}`}
                        alt="Article eyecatch"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          marginBottom: '20px',
                        }}
                      />
                    )}
                    <div>
                      <ArticleContent article={article} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="article_title">{article.title}</div>
                    <Skeleton
                      loading={true}
                      active
                      paragraph={{
                        rows: 4,
                        width: ['100%', '100%', '100%', '100%'],
                      }}
                      title={{ width: '20%' }}
                      style={{ marginBottom: '36px' }}
                    >
                      <div style={{ marginBottom: '20px' }}>
                        <Skeleton.Paragraph
                          style={{ width: 200 }}
                          active
                          size="small"
                        />
                        <br />
                        <Skeleton.Paragraph
                          style={{ width: 150, marginTop: 16 }}
                          active
                          size="small"
                        />
                      </div>
                    </Skeleton>
                    <div
                      className="content-structure-wrap"
                      style={{ marginBottom: '24px' }}
                    >
                      <div className="table-of-contents">
                        <div className="toc-title">この記事の目次</div>
                        {article &&
                          article.structured_headings &&
                          renderStructuredHeadings(article.structured_headings)}
                      </div>
                    </div>
                    {article && article.structured_headings ? (
                      <Title
                        level={3}
                        style={{
                          fontSize: '14px',
                          color: 'rgb(0 212 255)',
                          marginBottom: '16px',
                        }}
                      >
                        想定文字数：
                        <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                          {(article.structured_headings.split('###').length -
                            1) *
                            400}
                        </span>
                        文字
                      </Title>
                    ) : (
                      <Title
                        level={3}
                        style={{
                          fontSize: '14px',
                          color: 'rgb(0 212 255)',
                          marginBottom: '16px',
                        }}
                      >
                        想定文字数：計算不可
                      </Title>
                    )}
                    <Paragraph>
                      <ul style={{ lineHeight: 2.0 }}>
                        <li>構成案に沿ってAIが本文を生成します。</li>
                        <li>文字数は目安となります。</li>
                        <li>本文生成のやり直しはできません。</li>
                      </ul>
                    </Paragraph>
                    <Button
                      type="primary"
                      size="large"
                      disabled={true}
                      style={{
                        background:
                          'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)',
                        border: 'none',
                        transition: 'all 0.3s',
                        width: '100%',
                        fontWeight: 'bold',
                      }}
                    >
                      記事の本文を生成する
                    </Button>
                  </>
                )}
              </Col>
            </Row>
          </>
        )}
      </div>
    );
  };

  const renderHeader = () => (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      {!screens.xl && (
        <div className="logo mobile-logo">
          <span>Ignite creativity with..</span>magicss
        </div>
      )}
      <div
        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
      >
        {isAuthenticated ? (
          <AuthenticatedMenu
            userProfile={userProfile}
            userRole={userRole}
            isMobile={!screens.xl}
          />
        ) : (
          <div className="registration_wrap">
            <Text className="hide-on-mobile" style={{ marginRight: '16px' }}>
              ようこそ、ゲストさん！
            </Text>
            <Button
              type="primary"
              onClick={showRegistrationModal}
              className="registration"
              onMouseEnter={e => {
                e.currentTarget.style.opacity = '0.7';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = '1.0';
              }}
            >
              無料登録する
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderSidebar = () => {
    const menuItems = [
      {
        key: '/seo-article',
        icon: <FormOutlined />,
        label: '新規生成',
      },
      {
        key: '/seo-article/list',
        icon: <DatabaseOutlined />,
        label: '生成結果一覧',
      },
    ];

    if (userRole === 'master' || userRole === 'admin') {
      menuItems.push({
        key: '/seo-article/observe_task',
        icon: <ClusterOutlined />,
        label: 'タスク監視',
      });
    }

    const modeMenu = (
      <Menu>
        <Menu.SubMenu
          key="seo-article"
          title="SEO記事生成"
          onTitleClick={() =>
            isAuthenticated ? navigate('/seo-article') : showRegistrationModal()
          }
        >
          <Menu.Item
            key="seo-article-new"
            onClick={() =>
              isAuthenticated
                ? navigate('/seo-article')
                : showRegistrationModal()
            }
          >
            新規生成
          </Menu.Item>
          <Menu.Item
            key="seo-article-list"
            onClick={() =>
              isAuthenticated
                ? navigate('/seo-article/list')
                : showRegistrationModal()
            }
          >
            生成結果一覧
          </Menu.Item>
        </Menu.SubMenu>
        <Menu.Item key="fact-check" disabled>
          <Tooltip title="一部のユーザー様にのみ試験提供中です。ご希望の場合はお問い合わせください。">
            <Space>
              <LockOutlined />
              ファクトチェック
            </Space>
          </Tooltip>
        </Menu.Item>
      </Menu>
    );

    return (
      <>
        <div className="logo side">
          <a
            href={`https://magicss.ai/?utm_source=app&utm_medium=shared&utm_content=${uuid}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <span>Ignite creativity with..</span>magicss
          </a>
        </div>
        <div className="mode-selector">
          <Dropdown overlay={modeMenu} trigger={['hover']}>
            <div className="mode-selector-toggle">
              <RobotOutlined style={{ marginRight: '4px' }} />
              SEO記事生成モード <CaretDownOutlined />
            </div>
          </Dropdown>
        </div>
        <Menu
          mode="inline"
          selectedKeys={['/seo-article/list']}
          style={{ height: '100%', borderRight: 0, paddingTop: '24px' }}
        >
          {isAuthenticated ? (
            // ログイン済みユーザー向けメニュー
            <>
              <Menu.Item key="/seo-article" icon={<FormOutlined />}>
                <Link to="/seo-article">新規生成</Link>
              </Menu.Item>
              <Menu.Item key="/seo-article/list" icon={<DatabaseOutlined />}>
                <Link to="/seo-article/list">生成結果一覧</Link>
              </Menu.Item>
              {(userRole === 'master' || userRole === 'admin') && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <Menu.Item
                    key="/seo-article/observe_task"
                    icon={<ClusterOutlined />}
                  >
                    <Link to="/seo-article/observe_task">タスク監視</Link>
                  </Menu.Item>
                </>
              )}
            </>
          ) : (
            // 非ログインユーザー向けメニュー（クリックするとログインモーダル表示）
            <>
              <Menu.Item
                key="/seo-article"
                icon={<FormOutlined />}
                onClick={showRegistrationModal}
              >
                新規生成
              </Menu.Item>
              <Menu.Item
                key="/seo-article/list"
                icon={<DatabaseOutlined />}
                onClick={showRegistrationModal}
              >
                生成結果一覧
              </Menu.Item>
            </>
          )}
        </Menu>
      </>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (isPasswordRequired) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.05)',
          backdropFilter: 'blur(5px)',
        }}
      >
        <Modal
          visible={isPasswordRequired}
          footer={null}
          closable={false}
          maskClosable={false}
          style={{ top: 20 }}
        >
          <div
            style={{
              padding: '24px',
            }}
          >
            <div
              className="logo"
              style={{
                textAlign: 'center',
                marginBottom: '24px',
              }}
            >
              magicss
            </div>
            <Title
              level={4}
              style={{
                textAlign: 'center',
                fontSize: '18px',
                marginBottom: '24px',
              }}
            >
              パスワードを入力してください
            </Title>
            <div
              style={{
                border: '2px solid #f5f5f5',
                backgroundColor: '#f5f5f5',
                transition: 'all 0.3s',
                borderRadius: '8px',
                marginBottom: '24px',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#eee';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onFocus={e => {
                e.currentTarget.style.border = '2px solid #0072ff';
              }}
              onBlur={e => {
                e.currentTarget.style.border = '2px solid #f5f5f5';
              }}
            >
              <Input.Password
                prefix={<LockOutlined />}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onPressEnter={handlePasswordSubmit}
                placeholder="パスワード"
                size="large"
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  outline: 'none',
                  padding: '8px',
                }}
              />
            </div>
            <Button
              type="primary"
              onClick={handlePasswordSubmit}
              block
              size="large"
              style={{
                background: 'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)',
                border: 'none',
                transition: 'all 0.3s',
                width: '100%',
                fontWeight: 'bold',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.opacity = '0.7';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = '1.0';
              }}
            >
              送信して生成結果を開く
            </Button>
          </div>
        </Modal>
      </div>
    );
  }

  if (!article) {
    return <Typography.Title level={3}>記事が見つかりません</Typography.Title>;
  }

  return (
    <>
      <Helmet>
        <title>{`「${article?.keyword || ''}」の生成結果 | SEO記事生成モード - magicss`}</title>
        <meta name="robots" content="noindex" />
        <meta
          property="og:title"
          content={`「${article?.keyword || ''}」の生成結果 | SEO記事生成モード - magicss`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={
            article?.eyecatch_image_url
              ? `${process.env.REACT_APP_API_URL}${article.eyecatch_image_url}`
              : `${process.env.PUBLIC_URL}/img/ogp.png`
          }
        />
        <meta
          property="og:description"
          content={`「${article?.keyword || ''}」のSEO記事生成結果です。`}
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          width={250}
          style={{
            height: '100vh',
            background: '#fff',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            paddingTop: '80px',
          }}
          breakpoint="xl"
          collapsedWidth={0}
          trigger={null}
        >
          {renderSidebar()}
        </Sider>
        <Layout
          style={{
            marginLeft: screens.xl ? 250 : 0,
            transition: 'margin 0.2s',
          }}
        >
          <Header
            style={{
              background: '#fff',
              padding: screens.xl ? 0 : '0 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {!screens.xl && (
              <MenuOutlined className="menu-icon" onClick={toggleSidebar} />
            )}
            {renderHeader()}
          </Header>
          <Content
            style={{
              background: '#fff',
              padding: screens.xl ? '40px 40px 80px' : '16px',
              margin: screens.xl ? '24px 24px' : '16px 0',
              minHeight: 280,
            }}
          >
            <div id="a_detail">
              <Row gutter={24}>
                <Col xs={24} lg={14}>
                  <Breadcrumb style={{ margin: '16px 0' }}>
                    <Breadcrumb.Item>SEO記事生成</Breadcrumb.Item>
                    <Breadcrumb.Item>生成結果一覧</Breadcrumb.Item>
                    <Breadcrumb.Item className="hide-on-mobile">
                      「{article?.keyword}」の生成結果
                    </Breadcrumb.Item>
                  </Breadcrumb>
                  <Title
                    level={1}
                    className="gradient-text"
                    style={{ fontSize: '24px', lineHeight: '1.6' }}
                  >
                    「{article.keyword}」の生成結果
                  </Title>
                  <Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: '24px' }}
                  >
                    生成完了日:{' '}
                    {moment
                      .utc(article.completed_at)
                      .tz('Asia/Tokyo')
                      .format('YYYY/MM/DD HH:mm')}
                  </Text>
                </Col>
                <Col xs={24} lg={10} className="shared_label">
                  <Space direction="vertical" align="end" size="small">
                    <Tag
                      color="orange"
                      style={{ fontSize: '14px', padding: '4px 8px' }}
                    >
                      このページは共有された分析結果です
                    </Tag>
                  </Space>
                </Col>
              </Row>
              <Divider style={{ margin: '24px 0' }} />
              <div style={{ paddingBottom: screens.lg ? 0 : '60px' }}>
                {screens.lg ? (
                  <Tabs
                    activeKey={activeKey}
                    onChange={handleTabChange}
                    type="card"
                    size="middle"
                    style={{ width: '100%' }}
                  >
                    {tabItems.map(item => (
                      <TabPane
                        tab={
                          <Tooltip title={item.tooltip}>
                            <span>
                              {item.icon}
                              <span style={{ marginLeft: '8px' }}>
                                {item.title}
                              </span>
                            </span>
                          </Tooltip>
                        }
                        key={item.key}
                      >
                        {renderTabContent(item.key)}
                      </TabPane>
                    ))}
                  </Tabs>
                ) : (
                  <>
                    {renderTabContent(activeKey)}
                    <MobileTabMenu
                      activeKey={activeKey}
                      onChange={handleTabChange}
                      items={tabItems}
                    />
                  </>
                )}
              </div>
            </div>
          </Content>
          <Footer id="footer">
            <Row gutter={[16, 16]} justify={'space-between'}>
              <Col xs={24} xl={24} xxl={16}>
                <Row className="f_menu">
                  <Col xs={24} md={12} lg={5}>
                    <a href="https://magicss.ai" target="_blank">
                      magicssサービスサイト <ExportOutlined />
                    </a>
                  </Col>
                  <Col xs={24} md={12} lg={5}>
                    <a href="/terms-of-service" target="_blank">
                      サービス利用規約
                    </a>
                  </Col>
                  <Col xs={24} md={12} lg={5}>
                    <a href="/privacy-policy" target="_blank">
                      プライバシーポリシー
                    </a>
                  </Col>
                  <Col xs={24} md={12} lg={5}>
                    <a href="/legal-notice" target="_blank">
                      特定商取引法に基づく表記
                    </a>
                  </Col>
                  <Col xs={24} md={12} lg={4}>
                    <a href="/legal-notice" target="_blank">
                      お問い合わせ <ExportOutlined />
                    </a>
                  </Col>
                </Row>
              </Col>
              <Col
                xs={24}
                lg={24}
                xl={24}
                xxl={4}
                style={{ textAlign: 'center' }}
              >
                © 2024{' '}
                <a href="https://oneword.co.jp" target="_blank">
                  ONEWORD Inc.
                </a>
              </Col>
            </Row>
          </Footer>
        </Layout>
        <Drawer
          placement="left"
          closable={false}
          onClose={toggleSidebar}
          visible={sidebarVisible}
          styles={{ body: { padding: 0 } }}
          width={250}
        >
          {renderSidebar()}
        </Drawer>
      </Layout>
      <Modal
        title="magicss無料ユーザー登録"
        visible={isModalVisible}
        onOk={handleRegistration}
        onCancel={() => setIsModalVisible(false)}
        okText="無料登録する"
      >
        <p>
          magicssのSEO記事生成モードは、10種類の言語モデル×数百回のプロンプトによって「高度な分析＆ソース付きの本文生成」を自動実行します。
        </p>
        <ul>
          <li>検索意図の徹底分析</li>
          <li>最適化されたタイトルと構成案</li>
          <li>E-E-A-Tの高い本文生成</li>
        </ul>
        <p>ユーザー登録は3分で完了します。現在、一記事無料で生成可能！</p>
      </Modal>
    </>
  );
};

export default SharedArticle;
